import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { itineraryApi } from '../services/api';
import { Compass, Plus, X, Loader2, Sparkles, Globe, Check } from 'lucide-react';

export const PublishTemplateDialog = ({ open, onOpenChange, itineraryId, tripInput, generatedItinerary }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [highlights, setHighlights] = useState([]);
  const [newHighlight, setNewHighlight] = useState('');
  const [publishing, setPublishing] = useState(false);
  const [published, setPublished] = useState(false);

  useEffect(() => {
    if (!open || !tripInput) return;
    // Auto-generate defaults from itinerary data
    const city = tripInput.destination_city || '';
    const days = generatedItinerary?.days || [];
    const duration = days.length || 5;
    const styles = (tripInput.travel_styles || []).join(' & ');
    setTitle(`${duration}-Day ${city} ${styles} Trip`);
    setDescription(`Discover ${city}, ${tripInput.destination_country || ''} — a ${duration}-day ${tripInput.budget || ''} ${tripInput.traveler_type?.toLowerCase() || ''} trip featuring ${styles.toLowerCase()} experiences.`);

    // Extract highlights from day titles and key activities
    const autoHighlights = [];
    days.forEach(day => {
      if (day.title) autoHighlights.push(day.title.replace(/^Day \d+[:\s]*/i, '').trim());
    });
    setHighlights(autoHighlights.slice(0, 5));
    setPublished(false);
  }, [open, tripInput, generatedItinerary]);

  const addHighlight = () => {
    const h = newHighlight.trim();
    if (h && !highlights.includes(h) && highlights.length < 8) {
      setHighlights(prev => [...prev, h]);
      setNewHighlight('');
    }
  };

  const removeHighlight = (idx) => setHighlights(prev => prev.filter((_, i) => i !== idx));

  const handlePublish = async () => {
    if (!title.trim()) { toast.error('Please enter a title'); return; }
    if (!description.trim()) { toast.error('Please enter a description'); return; }
    if (highlights.length === 0) { toast.error('Add at least one highlight'); return; }
    setPublishing(true);
    try {
      const res = await itineraryApi.publishTemplate({
        itinerary_id: itineraryId,
        title: title.trim(),
        description: description.trim(),
        highlights,
      });
      if (res.data.success) {
        setPublished(true);
        toast.success('Trip published as a community template!');
      }
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to publish template';
      toast.error(msg);
    } finally { setPublishing(false); }
  };

  if (published) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md" data-testid="publish-success-dialog">
          <div className="text-center py-6 space-y-4">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
              <Check className="w-8 h-8 text-emerald-600" />
            </div>
            <h3 className="font-serif text-xl text-gray-900">Published!</h3>
            <p className="text-sm text-gray-500">Your trip is now available in the community templates library for other travelers to discover.</p>
            <Button onClick={() => onOpenChange(false)} className="bg-ocean hover:bg-ocean-light text-white rounded-full px-6" data-testid="close-publish-success">
              Done
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="publish-template-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-serif text-xl">
            <Compass className="w-5 h-5 text-gold" /> Share as Community Template
          </DialogTitle>
        </DialogHeader>
        <p className="text-xs text-gray-500 -mt-1">Let other travelers use your trip as a starting point.</p>

        <div className="space-y-4 mt-2">
          {/* Title */}
          <div>
            <label className="text-xs font-semibold text-gray-700 mb-1 block">Template Title</label>
            <Input
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="e.g., 5-Day Tokyo Culture & Food Trail"
              className="text-sm"
              maxLength={80}
              data-testid="publish-title-input"
            />
          </div>

          {/* Description */}
          <div>
            <label className="text-xs font-semibold text-gray-700 mb-1 block">Description</label>
            <Textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Describe what makes this trip special..."
              className="text-sm resize-none"
              rows={3}
              maxLength={300}
              data-testid="publish-description-input"
            />
            <p className="text-[10px] text-gray-400 text-right mt-0.5">{description.length}/300</p>
          </div>

          {/* Highlights */}
          <div>
            <label className="text-xs font-semibold text-gray-700 mb-1 block">
              Highlights <span className="text-gray-400 font-normal">({highlights.length}/8)</span>
            </label>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {highlights.map((h, i) => (
                <span key={`interest-${i}`} className="inline-flex items-center gap-1 text-xs px-2.5 py-1 bg-gold/10 text-ocean rounded-full border border-gold/20">
                  <Sparkles className="w-3 h-3 text-gold" /> {h}
                  <button onClick={() => removeHighlight(i)} className="ml-0.5 hover:text-red-500">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
            {highlights.length < 8 && (
              <div className="flex gap-2">
                <Input
                  value={newHighlight}
                  onChange={e => setNewHighlight(e.target.value)}
                  placeholder="Add a highlight..."
                  className="text-sm flex-1"
                  onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addHighlight())}
                  data-testid="publish-highlight-input"
                />
                <Button type="button" size="sm" variant="outline" onClick={addHighlight} className="shrink-0 rounded-full" data-testid="add-highlight-btn">
                  <Plus className="w-3.5 h-3.5" />
                </Button>
              </div>
            )}
          </div>

          {/* Preview Info */}
          <div className="bg-sky/5 rounded-lg p-3 flex items-center gap-3 border border-sky/10">
            <Globe className="w-8 h-8 text-ocean/40 shrink-0" />
            <div className="text-xs text-gray-500">
              <p><span className="font-semibold text-gray-700">{tripInput?.destination_city}</span>, {tripInput?.destination_country}</p>
              <p>{generatedItinerary?.days?.length || '?'} days &bull; {tripInput?.budget} &bull; {tripInput?.traveler_type}</p>
            </div>
          </div>

          {/* Publish Button */}
          <Button
            onClick={handlePublish}
            disabled={publishing}
            className="w-full bg-gold hover:bg-gold-light text-ocean rounded-full font-semibold py-3 shadow-lg"
            data-testid="publish-template-btn"
          >
            {publishing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Compass className="w-4 h-4 mr-2" />}
            {publishing ? 'Publishing...' : 'Publish to Community'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
